import React, { useCallback, useEffect, useMemo, useState } from 'react';
import platformApi, { platformErrorMessage } from '../services/platformApi';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import { useToast } from '../contexts/ToastContext';

const BASE = '/platform/whatsapp-notifications';

const STATUS_BADGE = {
  APPROVED: ['active', 'Approved'],
  PENDING: ['pending', 'In review'],
  IN_APPEAL: ['pending', 'In appeal'],
  DRAFT: ['inactive', 'Draft'],
  REJECTED: ['failed', 'Rejected'],
  PAUSED: ['grace', 'Paused'],
  DISABLED: ['failed', 'Disabled'],
};

function TemplateStatus({ status, reason, category }) {
  if (!status) return <span className="cell-muted">Not created</span>;
  const [variant, label] = STATUS_BADGE[status] || ['inactive', status];
  return (
    <>
      <span className={`badge badge--${variant}`}>{label}</span>
      {category && category !== 'UTILITY' && (
        <div style={{ fontSize: 12, marginTop: 4, color: 'var(--color-danger-text)' }}>
          Meta classed it as {category.toLowerCase()} — not sent. Use a utility template.
        </div>
      )}
      {reason && reason !== 'NONE' && <div className="cell-muted" style={{ fontSize: 12, marginTop: 4 }}>{reason.replace(/_/g, ' ').toLowerCase()}</div>}
    </>
  );
}

function preview(event) {
  return event.body.replace(/\{\{(\d+)\}\}/g, (_, n) => event.example[Number(n) - 1] ?? `{{${n}}}`);
}

function WhatsAppText({ text }) {
  return text.split(/(\*[^*\n]+\*)/g).map((part, i) => (
    /^\*[^*\n]+\*$/.test(part) ? <strong key={i}>{part.slice(1, -1)}</strong> : part
  ));
}

function MessagePreview({ event }) {
  return (
    <div style={{ marginTop: 6, padding: '8px 10px', background: '#f0fdf4', border: '1px solid #dcfce7', borderRadius: 8, maxWidth: 360 }}>
      {event.header && <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{event.header}</div>}
      <p style={{ fontSize: 13, whiteSpace: 'pre-wrap', margin: 0 }}><WhatsAppText text={preview(event)} /></p>
      {event.footer && <div className="cell-muted" style={{ fontSize: 11, marginTop: 6 }}>{event.footer}</div>}
      {event.buttonText && (
        <div style={{ borderTop: '1px solid #dcfce7', marginTop: 8, paddingTop: 6, textAlign: 'center', fontSize: 13, color: '#0284c7', fontWeight: 600 }}>
          ↗ {event.buttonText}
        </div>
      )}
    </div>
  );
}

function formFrom(overview) {
  const s = overview?.settings || {};
  return {
    enabled: Boolean(s.enabled),
    includePartnerCustomers: Boolean(s.includePartnerCustomers),
    senderTenantId: s.senderTenantId || '',
    senderChannelId: s.senderChannelId || '',
    events: Object.fromEntries((overview?.events || []).map((e) => [e.key, {
      enabled: e.enabled,
      templateName: e.templateName,
      language: e.language,
    }])),
  };
}

function WhatsAppNotifications() {
  const toast = useToast();
  const [overview, setOverview] = useState(null);
  const [form, setForm] = useState(() => formFrom(null));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [test, setTest] = useState({ event: '', phone: '' });

  const apply = useCallback((data) => {
    setOverview(data);
    setForm(formFrom(data));
  }, []);

  useEffect(() => {
    platformApi.get(BASE)
      .then((res) => apply(res.data?.data))
      .catch((error) => setLoadError(platformErrorMessage(error, 'Failed to load WhatsApp notifications')))
      .finally(() => setLoading(false));
  }, [apply]);

  const approved = useMemo(
    () => (overview?.events || []).filter((e) => e.templateStatus === 'APPROVED'),
    [overview],
  );
  const missing = (overview?.events || []).filter((e) => !e.templateStatus || e.templateStatus === 'DRAFT').length;

  const setEvent = (key, field, value) => setForm((prev) => ({
    ...prev,
    events: { ...prev.events, [key]: { ...prev.events[key], [field]: value } },
  }));

  const run = async (name, request, onSuccess) => {
    setBusy(name);
    try {
      const res = await request();
      onSuccess(res.data?.data);
    } catch (error) {
      toast.error(platformErrorMessage(error, 'Request failed'));
    } finally {
      setBusy(null);
    }
  };

  const save = (e) => {
    e.preventDefault();
    const custom = form.senderTenantId.trim() && form.senderChannelId.trim();
    run('save', () => platformApi.put(BASE, {
      ...form,
      senderTenantId: custom ? form.senderTenantId.trim() : null,
      senderChannelId: custom ? form.senderChannelId.trim() : null,
    }), (data) => {
      apply(data);
      toast.success(data?.settings?.enabled ? 'Saved. Billing messages will also go out on WhatsApp.' : 'Saved. WhatsApp messages are off.');
    });
  };

  const createTemplates = () => run('create', () => platformApi.post(`${BASE}/templates`), (data) => {
    apply(data);
    const results = data?.results || [];
    const created = results.filter((r) => r.created).length;
    const failed = results.filter((r) => r.error);
    if (failed.length) {
      toast.error(`${failed.length} template(s) failed: ${failed.map((r) => `${r.templateName} — ${r.error}`).join('; ')}`);
    } else {
      toast.success(created ? `Submitted ${created} template(s) to Meta for review.` : 'All templates already exist.');
    }
  });

  const refresh = () => run('refresh', () => platformApi.post(`${BASE}/templates/refresh`), (data) => {
    apply(data);
    toast.success('Template status refreshed from Meta.');
  });

  const sendTest = (e) => {
    e.preventDefault();
    run('test', () => platformApi.post(`${BASE}/test`, test), (data) => {
      toast.success(`Test message sent to ${data?.to || 'the number'}.`);
    });
  };

  if (loading) return <LoadingScreen message="Loading WhatsApp notifications..." />;

  const sender = overview?.sender;
  const channels = overview?.channels || [];

  return (
    <div className="page">
      <PageHeader
        title="WhatsApp notifications"
        description="Billing messages (payments, refunds, renewals, trials, plan changes) sent to each workspace's billing admins on WhatsApp, alongside the email. Sign-up OTP and welcome messages are set under WhatsApp Config."
      />
      {loadError && <div className="alert alert--error">{loadError}</div>}

      <form className="card card__body" onSubmit={save} style={{ marginBottom: 24 }}>
        <h2 className="section-title">Delivery</h2>
        <div className="form-group">
          <label htmlFor="wa-enabled" className="form-checkbox">
            <input
              id="wa-enabled"
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
            <span>Send billing messages on WhatsApp</span>
          </label>
          <p className="form-hint">Only approved templates are sent. Recipients are the workspace's billing admins with a mobile number on their profile.</p>
        </div>
        <div className="form-group">
          <label htmlFor="wa-partners" className="form-checkbox">
            <input
              id="wa-partners"
              type="checkbox"
              checked={form.includePartnerCustomers}
              onChange={(e) => setForm((prev) => ({ ...prev, includePartnerCustomers: e.target.checked }))}
            />
            <span>Also message partner (white-label) customers</span>
          </label>
          <p className="form-hint">Off by default: these messages come from the Inaiy number and mention Inaiy.</p>
        </div>

        <h2 className="section-title">Sender</h2>
        {sender ? (
          <p className="form-hint" style={{ marginBottom: 12 }}>
            Sending from <strong>{sender.displayName || 'WhatsApp'}</strong>
            {sender.phoneNumber ? ` (${sender.phoneNumber})` : ''} —{' '}
            <span className={`badge badge--${sender.ready ? 'active' : 'failed'}`}>{sender.ready ? 'Connected' : (sender.status || 'Not ready')}</span>
            {sender.source === 'account_whatsapp' ? ' · same channel as the sign-up OTP (WhatsApp Config).' : ' · custom channel.'}
          </p>
        ) : (
          <div className="alert alert--error">No sender channel found. Set a default in WhatsApp Config or enter a workspace and channel below.</div>
        )}
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label" htmlFor="wa-tenant">Sender workspace ID (optional)</label>
            <input
              id="wa-tenant"
              className="form-input cell-mono"
              value={form.senderTenantId}
              placeholder={sender?.tenantId || '24-character tenant ID'}
              maxLength={24}
              onChange={(e) => setForm((prev) => ({ ...prev, senderTenantId: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="wa-channel">Sender channel</label>
            <select
              id="wa-channel"
              className="form-select"
              value={form.senderChannelId}
              onChange={(e) => setForm((prev) => ({
                ...prev,
                senderChannelId: e.target.value,
                senderTenantId: e.target.value ? (prev.senderTenantId || sender?.tenantId || '') : '',
              }))}
            >
              <option value="">Same as WhatsApp Config (sign-up OTP)</option>
              {channels.map((c) => (
                <option key={c.channelId} value={c.channelId}>
                  {c.displayName || 'Channel'} {c.phoneNumber ? `· ${c.phoneNumber}` : ''} · {String(c.status || '').toLowerCase()}
                </option>
              ))}
              {form.senderChannelId && !channels.some((c) => c.channelId === form.senderChannelId) && (
                <option value={form.senderChannelId}>{form.senderChannelId}</option>
              )}
            </select>
          </div>
        </div>

        <div className="card__header" style={{ padding: '16px 0 8px', borderBottom: 0 }}>
          <h2 className="section-title" style={{ margin: 0 }}>Messages</h2>
          <div className="btn-group">
            <button type="button" className="btn btn--secondary btn--sm" onClick={refresh} disabled={!sender?.ready || busy}>
              {busy === 'refresh' ? 'Refreshing…' : 'Refresh status'}
            </button>
            <button type="button" className="btn btn--primary btn--sm" onClick={createTemplates} disabled={!sender?.ready || busy || !missing}>
              {busy === 'create' ? 'Submitting…' : missing ? `Create ${missing} template(s) on Meta` : 'Templates created'}
            </button>
          </div>
        </div>
        <div className="table-wrapper">
          <table className="data-table data-table--responsive">
            <thead>
              <tr>
                <th>Send</th>
                <th>Notification</th>
                <th>Template</th>
                <th>Meta status</th>
              </tr>
            </thead>
            <tbody>
              {(overview?.events || []).map((event) => {
                const config = form.events[event.key] || {};
                return (
                  <tr key={event.key}>
                    <td data-label="Send">
                      <input
                        type="checkbox"
                        aria-label={`Send ${event.label}`}
                        checked={Boolean(config.enabled)}
                        onChange={(e) => setEvent(event.key, 'enabled', e.target.checked)}
                      />
                    </td>
                    <td data-label="Notification" style={{ maxWidth: 420 }}>
                      <div style={{ fontWeight: 600 }}>{event.label}</div>
                      <div className="cell-muted" style={{ fontSize: 12 }}>{event.description}</div>
                      <details style={{ marginTop: 6 }}>
                        <summary className="cell-muted" style={{ fontSize: 12, cursor: 'pointer' }}>Preview</summary>
                        <MessagePreview event={event} />
                      </details>
                    </td>
                    <td data-label="Template">
                      <input
                        className="form-input cell-mono"
                        style={{ minWidth: 200, fontSize: 13 }}
                        aria-label={`${event.label} template name`}
                        value={config.templateName || ''}
                        onChange={(e) => setEvent(event.key, 'templateName', e.target.value.trim())}
                      />
                      <input
                        className="form-input cell-mono"
                        style={{ width: 90, fontSize: 13, marginTop: 6 }}
                        aria-label={`${event.label} template language`}
                        value={config.language || ''}
                        onChange={(e) => setEvent(event.key, 'language', e.target.value.trim())}
                      />
                    </td>
                    <td data-label="Meta status">
                      <TemplateStatus status={event.templateStatus} reason={event.rejectionReason} category={event.templateCategory} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="form-hint">
          New templates are submitted as Utility templates on the sender channel; Meta usually reviews them within minutes to a few hours.
          They also appear on that workspace's Templates page. Changing a template name points the message at an existing approved template instead.
        </p>
        <div className="btn-group" style={{ justifyContent: 'flex-end' }}>
          <button type="submit" className="btn btn--primary" disabled={Boolean(busy)}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
        </div>
      </form>

      <form className="card card__body" onSubmit={sendTest}>
        <h2 className="section-title">Send a test</h2>
        {approved.length === 0 ? (
          <p className="form-hint">Tests are available once Meta approves at least one template.</p>
        ) : (
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="wa-test-event">Message</label>
              <select
                id="wa-test-event"
                className="form-select"
                value={test.event}
                required
                onChange={(e) => setTest((prev) => ({ ...prev, event: e.target.value }))}
              >
                <option value="">Choose a message</option>
                {approved.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="wa-test-phone">WhatsApp number</label>
              <input
                id="wa-test-phone"
                className="form-input"
                value={test.phone}
                required
                inputMode="tel"
                placeholder="+91 98765 43210"
                onChange={(e) => setTest((prev) => ({ ...prev, phone: e.target.value }))}
              />
            </div>
          </div>
        )}
        {approved.length > 0 && (
          <div className="btn-group" style={{ justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn--secondary" disabled={Boolean(busy)}>{busy === 'test' ? 'Sending…' : 'Send test'}</button>
          </div>
        )}
      </form>
    </div>
  );
}

export default WhatsAppNotifications;
